import type { DashboardJobWorkload, JobConfig, JobStatus } from "@cv-screener/shared";

export type JobSortKey = "job" | "department" | "status" | "criteria" | "opening" | "closing" | "received" | "processed" | "pending" | "error";
export type JobSortDirection = "ascending" | "descending";

export type JobSort = {
  key: JobSortKey;
  direction: JobSortDirection;
};

/** Operational priority, not alphabetical order: active intake first, then work
 * needing action, temporarily paused work, and finally historical records. */
export const JOB_STATUS_SORT_ORDER: Record<JobStatus, number> = {
  Open: 0,
  Draft: 1,
  Paused: 2,
  Closed: 3,
  Archived: 4,
};

export function jobMatchesSearch(job: JobConfig, search: string): boolean {
  const query = search.trim().toLocaleLowerCase();
  if (!query) return true;
  return [job.jobId, job.jobTitle, job.department, job.location]
    .some((value) => value.toLocaleLowerCase().includes(query));
}

export function defaultJobSortDirection(key: JobSortKey): JobSortDirection {
  return ["criteria", "received", "processed", "pending", "error"].includes(key) ? "descending" : "ascending";
}

export function updateJobSorts(current: JobSort[], key: JobSortKey, makePrimary: boolean): JobSort[] {
  if (makePrimary) return [{ key, direction: defaultJobSortDirection(key) }];
  const existingIndex = current.findIndex((sort) => sort.key === key);
  if (existingIndex < 0) return [...current, { key, direction: defaultJobSortDirection(key) }];
  const existing = current[existingIndex]!;
  if (existing.direction !== defaultJobSortDirection(key)) return current.filter((_, index) => index !== existingIndex);
  return current.map((sort, index) => index === existingIndex
    ? { ...sort, direction: sort.direction === "descending" ? "ascending" : "descending" }
    : sort);
}

function jobSortValue(job: JobConfig, key: JobSortKey, workload?: DashboardJobWorkload): string | number {
  if (key === "job") return `${job.jobTitle} ${job.jobId}`.toLocaleLowerCase();
  if (key === "department") return job.department.toLocaleLowerCase();
  if (key === "status") return JOB_STATUS_SORT_ORDER[job.status];
  if (key === "criteria") return Number(job.markdownVersion);
  if (key === "opening") return new Date(job.openingDate || job.createdAt).getTime();
  if (key === "closing") return job.closingDate ? new Date(job.closingDate).getTime() : Number.POSITIVE_INFINITY;
  if (key === "received") return workload?.receivedApplications ?? 0;
  if (key === "processed") return workload?.processedApplications ?? 0;
  if (key === "pending") return workload?.pendingApplications ?? 0;
  return workload?.errorApplications ?? 0;
}

export function sortJobs(jobs: JobConfig[], workloads: Map<string, DashboardJobWorkload>, sorts: JobSort[]): JobConfig[] {
  return jobs.map((job, index) => ({ job, index })).sort((first, second) => {
    for (const sort of sorts) {
      const left = jobSortValue(first.job, sort.key, workloads.get(first.job.jobId));
      const right = jobSortValue(second.job, sort.key, workloads.get(second.job.jobId));
      const comparison = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right));
      if (comparison !== 0) return sort.direction === "descending" ? -comparison : comparison;
    }
    return first.index - second.index;
  }).map(({ job }) => job);
}
